import React, { useEffect } from "react";
import ProjectCard from "./ProjectCard";
import { caseStudiesData } from "./caseStudiesData";
import { useNavigate, useParams } from "react-router-dom";
import ThreeDCarousel from "./ThreeDCarousel";
import "./CaseStudies.css";
import {
  FaChartPie,
  FaCoins,
  FaCubes,
  FaImage,
  FaLeaf,
  FaProjectDiagram,
  FaRobot,
  FaWallet,
} from "react-icons/fa";

const iconMap = {
  wallet: <FaWallet />,
  nft: <FaImage />,
  bot: <FaRobot />,
  dapp: <FaProjectDiagram />,
  carbon: <FaLeaf />,
  defi: <FaChartPie />, // ✅ fixed
  blockchain: <FaCubes />,
  staking: <FaCoins />,
};
const ProjectDetailView = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  // ✅ Correct project fetch
  const project = caseStudiesData.find((p) => p.id === Number(id));

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  // ✅ Safety fallback
  if (!project) {
    return <div style={{ padding: "40px" }}>Project not found</div>;
  }

  // ✅ Related projects
  const relatedProjects = caseStudiesData
    .filter((p) => !p.isPromo && p.id !== project.id)
    .slice(0, 3);

  // ✅ Image fix
  const projectImg = project.image
    ? project.image.startsWith("http") || project.image.startsWith("/")
      ? project.image
      : `/${project.image}`
    : "/assets/img/case-studies/web3_sphere.png";

  return (
    <div className="cs-project-detail">
      <div className="relative w-full pb-20">
        <div className="cs-pd-cover"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-tr from-blue-500/20 to-purple-500/20 blur-xl rounded-2xl opacity-70 group-hover:opacity-100 transition"></div>

              <div
                className="relative w-20 h-20 rounded-2xl flex items-center justify-center shadow-md group-hover:shadow-xl transition
    bg-gradient-to-br from-[rgb(33,112,233)] to-[rgb(128,79,252)] text-white"
              >
                <div className="text-3xl group-hover:scale-110 transition">
                  {iconMap[project.titleicon] || <FaCubes />}
                </div>
              </div>
            </div>

            <div className="flex-1">
              <div className="flex flex-wrap gap-2 mb-4">
                {project.tags?.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wider rounded-full
              bg-gradient-to-r from-sky-100 to-blue-100 text-sky-700 border border-sky-200
              shadow-sm hover:shadow-md hover:scale-105 transition"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <h1
                className="text-3xl md:text-4xl lg:text-5xl font-extrabold leading-tight tracking-tight
          bg-gradient-to-r from-[#000662] via-blue-700 to-cyan-500 bg-clip-text text-transparent"
              >
                {project.title}
              </h1>

              <p className="mt-3 text-slate-500 text-sm md:text-base max-w-2xl">
                Build scalable, high-performance blockchain solutions with
                modern UI and seamless user experience.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CAROUSEL SECTION */}
      <div className="w-full px-4 md:px-12">
        <ThreeDCarousel images={project.projectimage || []} />
      </div>

      {/* CONTENT CARD SECTION */}
      <div className="cs-container pb-20">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white border border-slate-100 rounded-[32px] p-8 md:p-12 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.05)]">
            {/* OVERVIEW */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-[#000662] mb-6 flex items-center gap-3">
                Overview
              </h2>

              <div className="space-y-4">
                {Array.isArray(project.description) ? (
                  project.description.map((desc, index) => (
                    <p
                      key={index}
                      className="text-slate-600 text-lg leading-relaxed"
                    >
                      {desc}
                    </p>
                  ))
                ) : (
                  <p className="text-slate-600 text-lg leading-relaxed">
                    {project.description}
                  </p>
                )}
              </div>
            </section>

            {/* DIVIDER */}
            <div className="h-px bg-slate-100 w-full mb-12"></div>

            {/* SOLUTION */}
            <section>
              <h2 className="text-2xl font-bold text-[#000662] mb-6">
                Solution
              </h2>

              {project.solution?.intro && (
                <p className="text-slate-600 text-lg leading-relaxed mb-8 italic">
                  {project.solution.intro}
                </p>
              )}

              {project.solution?.points?.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {project.solution.points.map((item, index) =>
                    item.title && item.desc ? (
                      <div
                        key={index}
                        className="flex gap-4 p-5 bg-slate-50/50 rounded-2xl border border-slate-100 hover:border-blue-200 transition-colors group"
                      >
                        <div className="w-2 h-2 mt-2.5 rounded-full bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.5)] group-hover:scale-125 transition-transform"></div>
                        <div>
                          <h4 className="font-bold text-slate-800 mb-1">
                            {item.title}
                          </h4>
                          <p className="text-slate-600 text-sm leading-relaxed">
                            {item.desc}
                          </p>
                        </div>
                      </div>
                    ) : null,
                  )}
                </div>
              )}

              {project.solution?.summary && (
                <div className="mt-12 p-8 bg-blue-50/30 rounded-3xl border border-blue-100">
                  <h3 className="text-xl font-bold text-[#000662] mb-4">
                    Summary
                  </h3>
                  <p className="text-slate-700 leading-relaxed italic">
                    {project.solution.summary}
                  </p>
                </div>
              )}
            </section>
          </div>
        </div>

        {/* RELATED PROJECTS */}
        <div className="cs-pd-footer-section pt-5">
          <div style={{ marginBottom: "36px" }}>
            <h2>Web3 dapps related to {project.title}</h2>
            <p style={{ marginTop: "8px" }}>
              Discover other blockchain applications.
            </p>
          </div>

          <div className="cs-grid">
            {relatedProjects.map((relProj, idx) => (
              <ProjectCard
                key={relProj.id}
                project={relProj}
                index={idx}
                onClick={(p) => navigate(`/case-studies/${p.id}`)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetailView;
